/**
 * Lowercases and strips accents so "Déménagement" matches a "demenagement"
 * query. Built without a regex so the combining-mark range stays readable.
 */
export const deburr = (value: string): string => {
  let output = '';
  for (const char of value.normalize('NFD')) {
    const code = char.codePointAt(0) ?? 0;
    if (code >= 0x0300 && code <= 0x036f) continue; // combining diacritical marks
    output += char;
  }
  return output.toLowerCase();
};

export const initialsOf = (name: string | null | undefined, fallback = '?') => {
  const value = (name ?? '').trim();
  if (!value) return fallback;
  return value
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

export const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

export const formatNumber = (value: number) =>
  Math.round(value).toLocaleString('fr-FR').replace(/\s/g, ' ');

export const formatDuration = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * Accepts the Moroccan phone formats the app's users type and normalises them
 * to E.164, or returns null when the value cannot be a phone number.
 */
export const normalizePhone = (raw: string): string | null => {
  const value = raw.trim();
  if (!value) return null;

  let cleaned = value.replace(/[\s().-]/g, '');
  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`;
  }

  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1).replace(/\D/g, '');
    return digits.length >= 8 ? `+${digits}` : null;
  }

  const digitsOnly = cleaned.replace(/\D/g, '');
  if (!digitsOnly) return null;

  if (digitsOnly.startsWith('212') && digitsOnly.length === 12) {
    return `+${digitsOnly}`;
  }
  if (digitsOnly.length === 10 && digitsOnly.startsWith('0')) {
    return `+212${digitsOnly.slice(1)}`;
  }
  if (digitsOnly.length === 9 && /^[67]/.test(digitsOnly)) {
    return `+212${digitsOnly}`;
  }

  return digitsOnly.length >= 8 ? `+${digitsOnly}` : null;
};

/**
 * A booking's preferred slot for display: "jeu. 25 sept. · 10:00". The date is
 * a plain `yyyy-mm-dd` column, parsed as a local date so it never shifts a day.
 */
export const formatSlot = (date: string | null | undefined, time?: string | null) => {
  let day = '';
  if (date) {
    const [year, month, dayOfMonth] = date.split('-').map(Number);
    day = new Date(year, month - 1, dayOfMonth).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }
  const parts = [day, time ?? ''].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Date à définir';
};
